import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, AlertCircle, MessageSquare, FileText, Calendar, CreditCard, Star, PartyPopper, X } from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  action_required: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Action Required' },
  update: { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Update' },
  reminder: { color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', label: 'Reminder' },
  system: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'System' },
  celebration: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Celebration' },
};

const TYPE_ICON = {
  proposal_received: FileText,
  proposal_accepted: FileText,
  deposit_required: CreditCard,
  deposit_paid: CreditCard,
  booking_confirmed: Check,
  booking_completed: Star,
  booking_cancelled: AlertCircle,
  vendor_marked_complete: Check,
  new_inquiry: MessageSquare,
  vendor_message_received: MessageSquare,
  customer_message_received: MessageSquare,
  event_completed: PartyPopper,
  review_requested: Star,
};

const NotificationCard = ({ notification, onRead, onNavigate }) => {
  const Icon = TYPE_ICON[notification.type] || Bell;
  const cat = CATEGORY_CONFIG[notification.category] || CATEGORY_CONFIG.update;
  const timeAgo = notification.created_at
    ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
    : '';

  return (
    <button
      data-testid={`notification-${notification.id}`}
      onClick={() => {
        onRead(notification.id);
        onNavigate(notification.cta_route);
      }}
      className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        notification.is_read ? 'opacity-60' : ''
      }`}
    >
      <div className="flex gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${cat.bg} flex items-center justify-center mt-0.5`}>
          <Icon className={`w-4 h-4 ${cat.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <p className={`text-sm ${notification.is_read ? 'font-normal text-gray-600' : 'font-semibold text-gray-900'} truncate`}>
                {notification.title}
              </p>
              {notification.is_nudge && (
                <span className="flex-shrink-0 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                  Reminder
                </span>
              )}
            </div>
            {!notification.is_read && (
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] text-gray-400">{timeAgo}</span>
            {notification.cta_label && (
              <span className={`text-[11px] font-medium ${cat.color}`}>{notification.cta_label}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const dropdownRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notifications/unread-count`, { headers });
      setUnreadCount(res.data.total || 0);
    } catch { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 30 };
      if (activeTab === 'unread') params.unread_only = true;
      else if (activeTab !== 'all') params.category = activeTab;
      const res = await axios.get(`${API}/api/notifications`, { headers, params });
      setNotifications(res.data.notifications || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    fetchUnreadCount();
    const iv = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(iv);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const markRead = async (id) => {
    await axios.post(`${API}/api/notifications/${id}/read`, {}, { headers }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.post(`${API}/api/notifications/mark-all-read`, {}, { headers }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleNavigate = (route) => {
    setIsOpen(false);
    if (!route) return;
    // Defensive normalizer: legacy notifications (and a few stale backend
    // emitters) stored `/vendor-dashboard` (dash form). The canonical route
    // registered in App.js is `/vendor/dashboard` (slash form). Rewrite the
    // prefix so both historical DB rows and any stray new records route
    // correctly. Query strings and sub-paths are preserved.
    let target = route;
    if (target.startsWith('/vendor-dashboard')) {
      target = '/vendor/dashboard' + target.slice('/vendor-dashboard'.length);
    }
    // Bare vendor dashboard routes (no query string) are a no-op when the
    // vendor is already on the dashboard. Default them to the Requests tab
    // so the click always produces a visible route/tab change. This also
    // repairs historical DB records that stored a bare path.
    if (target === '/vendor/dashboard' || target === '/vendor/dashboard/') {
      target = '/vendor/dashboard?tab=inquiries';
    }
    navigate(target);
  };

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'action_required', label: 'Action' },
    { key: 'update', label: 'Updates' },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="notification-bell-btn"
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
        title="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          data-testid="notification-dropdown"
          className="fixed inset-x-0 top-12 mx-2 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:mx-0 w-auto sm:w-[380px] max-h-[500px] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={markAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 px-2">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'text-gray-900 border-gray-900'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[380px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10 px-4" data-testid="notification-empty-state">
                <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">We'll let you know when something happens</p>
              </div>
            ) : (
              notifications.map(n => (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  onRead={markRead}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
